import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { WelletContext } from '../../context/wellet/welletContext';
import { getSpecialDateById, createSpecialDate, updateSpecialDate, getAllShows } from '../../services';
import { Button, Loading, Toast } from '../../uiComponents/common';
import { PageHeader } from '../../components';
import { useCreateBreadCrumbs, useGetRestaurants } from '../../hooks';
import { defaultQueryOptions } from '../../utils/defaultQueryOptions';
import EventInfoSection from './components/eventInfoSection';
import EventProductList from './components/eventProductList';
import './fechasEspeciales.css';

const formatDateForApi = (date) => {
  if (!date) return null;
  if (typeof date === 'string') {
    return date.substring(0, 10);
  }
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const FechasEspecialesDetail = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'screens.fechasEspeciales' });
  const { t: tModal } = useTranslation('translation', { keyPrefix: 'modals.createEditEvent' });
  const { id } = useParams();
  const navigate = useNavigate();
  const welletContext = useContext(WelletContext);
  const organization = useSelector((state) => state.app.selectedOrganization);
  const organizationId =
    organization.id === organization.marketId ? null : Number(organization.id);

  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'group' ? 'group' : 'area';
  const isNew = id === 'new';
  const methods = useForm();
  const currentRestaurant = methods.watch('restaurant');
  const formInitialized = useRef(false);
  const prevRestaurantId = useRef(null);
  const [pendingProducts, setPendingProducts] = useState([]);
  const productListRef = useRef();

  const detailQuery = useQuery({
    queryKey: ['specialDate', id],
    queryFn: () => getSpecialDateById(welletContext, id),
    ...defaultQueryOptions,
    enabled: !isNew && !!id,
  });

  const restaurantsQuery = useGetRestaurants(organization, {
    queryKey: ['restaurantes', organization.marketId],
    queryFn: () => getAllShows(welletContext, organization.marketId, organizationId),
    keepPreviousData: true,
  });
  const restaurants = restaurantsQuery?.data?.data?.map((r) => ({
    label: r.name,
    value: r.id,
  }));

  const event = detailQuery?.data?.data || null;

  useEffect(() => {
    if (!isNew && event && restaurants && !formInitialized.current) {
      const matchedRestaurant = restaurants.find((r) => r.value === event.showId) || null;
      methods.reset({
        name: event.name || '',
        fromDate: event.fromDate || null,
        toDate: event.toDate || null,
        restaurant: matchedRestaurant,
      });
      setPendingProducts(event.products || []);
      formInitialized.current = true;
    }
  }, [event, restaurants]);

  // Clear products when restaurant changes in create mode
  useEffect(() => {
    if (!isNew) return;
    const restaurantValue = currentRestaurant?.value || null;
    if (prevRestaurantId.current !== null && prevRestaurantId.current !== restaurantValue) {
      setPendingProducts([]);
    }
    prevRestaurantId.current = restaurantValue;
  }, [currentRestaurant, isNew]);

  const updateMutation = useMutation({
    mutationFn: ({ formData, productPrices }) => {
      const showId = formData.restaurant?.value || organizationId || Number(organization.id);
      const body = {
        id: event.specialDateId,
        name: formData.name,
        fromDate: formatDateForApi(formData.fromDate),
        toDate: formatDateForApi(formData.toDate),
        showId,
        isActive: true,
        productPrices,
      };
      return updateSpecialDate(welletContext, body);
    },
    onSuccess: () => {
      detailQuery.refetch();
    },
  });

  const createMutation = useMutation({
    mutationFn: ({ formData, productPrices }) => {
      const showId = formData.restaurant?.value || organizationId || Number(organization.id);
      return createSpecialDate(welletContext, {
        name: formData.name,
        fromDate: formatDateForApi(formData.fromDate),
        toDate: formatDateForApi(formData.toDate),
        showId,
        isActive: true,
        productPrices,
      });
    },
    onSuccess: () => {
      navigate('/special-dates');
    },
  });

  const mutation = isNew ? createMutation : updateMutation;

  const onSubmit = async (data) => {
    if (!data.name || data.name.trim().length < 3) {
      methods.setError('name', { type: 'manual', message: tModal('validationNameMin') });
      return;
    }
    if (!data.fromDate || !data.toDate) {
      if (!data.fromDate) methods.setError('fromDate', { type: 'manual', message: '' });
      if (!data.toDate) methods.setError('toDate', { type: 'manual', message: '' });
      return;
    }
    if (isNew && !data.restaurant?.value) {
      methods.setError('restaurant', { type: 'manual', message: tModal('validationRestaurant') });
      return;
    }

    // Validate product prices via refs
    const productsValid = await productListRef.current?.validateAll();
    if (!productsValid) return;

    // Collect prices from product forms
    const productPrices = productListRef.current?.getAllPrices() || [];

    mutation.mutate({ formData: data, productPrices });
  };

  const handleToastClose = () => {
    mutation.reset();
  };

  const pageTitle = isNew ? t('detail.newTitle') : (event?.name || '...');
  const restaurantId = isNew ? (currentRestaurant?.value || null) : (event?.showId || null);

  const localQueryData = { products: pendingProducts };

  useCreateBreadCrumbs(
    [
      { name: t('title'), path: '/special-dates' },
      { name: pageTitle, path: `/special-dates/${id}` },
    ],
    pageTitle,
  );

  if (!isNew && detailQuery.isLoading) {
    return <Loading />;
  }

  if (!isNew && !event) {
    return <div className='fe-empty'>{t('empty')}</div>;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <PageHeader
          title={pageTitle}
          marginBottom={false}
          border={true}
          endComponent={
            <div className='d-flex align-items-center gap-2'>
              <Button
                size='medium'
                type='button'
                text={t('detail.cancel')}
                className='btn-wellet-secondary'
                onClick={() => navigate('/special-dates')}
              />
              <Button
                size='medium'
                type='submit'
                text={isNew ? t('detail.create') : t('detail.saveInfo')}
                className='btn-wellet-primary ml-2'
                loading={mutation.isLoading}
                disabled={mutation.isLoading}
              />
            </div>
          }
        />

        <EventInfoSection restaurants={restaurants} disableRestaurant={!isNew} />
      </form>

      <EventProductList
        ref={productListRef}
        specialDateId={id}
        restaurantId={restaurantId}
        queryData={localQueryData}
        isLoadingData={!isNew && detailQuery.isLoading}
        isNew={isNew}
        onPendingChange={setPendingProducts}
        initialTab={initialTab}
      />

      {mutation.isError && (
        <Toast
          message={
            mutation.error?.response?.data?.message === 'OVERLAPPING_SPECIAL_DATES'
              ? t('toast.errorOverlapping')
              : t('toast.error')
          }
          type='ERROR'
          onCallback={handleToastClose}
        />
      )}
      {mutation.isSuccess && !isNew && (
        <Toast message={t('toast.update')} type='SUCCESS' onCallback={handleToastClose} />
      )}
    </FormProvider>
  );
};

export default FechasEspecialesDetail;
